/**
 * MapDashboard.jsx
 * ================
 * Leaflet map with:
 *  - Esri World Imagery satellite tile layer
 *  - GeoJSON survey parcel polygons (colour-coded by crop/stage)
 *  - Survey number labels on each parcel
 *  - Click popup with full parcel details
 *  - Yellow parcel lines, red taluk/village borders (govt app style)
 *
 * Drone additions:
 *  - Live drone marker (pulsing red dot) tracking real-time GPS
 *  - Flight path polyline showing drone's route
 *  - Classification dots at each image capture location
 *  - Click classification dot → thumbnail + prediction popup
 *  - Socket.IO listener for "drone_position" and "new_classification"
 */

import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import {
  MapContainer, TileLayer, GeoJSON, Marker,
  Popup, useMap, ZoomControl, ScaleControl, Polyline, CircleMarker,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import parcelsData from '../data/parcels.json';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Try to connect Socket.IO — graceful fallback to polling
let io = null;
try { io = require('socket.io-client'); } catch { }

// ─── Colour Palettes ──────────────────────────────────────────────────────────
export const CROP_COLORS = {
  Paddy:     '#00FF00',
  Sugarcane: '#FFD700',
  Banana:    '#FF6B35',
  Coconut:   '#228B22',
  Groundnut: '#DEB887',
  Fallow:    '#8B4513',
};

export const STAGE_COLORS = {
  Early:        '#FFFF00',
  Intermediate: '#90EE90',
  Mature:       '#006400',
  Harvested:    '#A0522D',
};

const MAP_CENTER  = [10.748, 79.413];
const DEFAULT_ZOOM = 14;

// ─── Style Helpers ────────────────────────────────────────────────────────────
function getParcelStyle(feature, viewMode, selectedSurvey) {
  const props      = feature.properties;
  const isSelected = selectedSurvey && props.survey_number === selectedSurvey;
  const baseColor  = viewMode === 'crop'
    ? (CROP_COLORS[props.crop_type]    || '#94a3b8')
    : (STAGE_COLORS[props.growth_stage] || '#94a3b8');

  return {
    fillColor:   baseColor,
    fillOpacity: isSelected ? 0.75 : 0.45,
    color:       isSelected ? '#ffffff' : '#FFD700',   // yellow parcel lines
    weight:      isSelected ? 3 : 1.5,
    opacity:     1,
  };
}

// ─── Survey Label ─────────────────────────────────────────────────────────────
function SurveyLabel({ feature }) {
  const map = useMap();
  const coords = feature.geometry.coordinates[0];
  // Compute centroid of polygon
  const lats = coords.map(c => c[1]);
  const lons = coords.map(c => c[0]);
  const lat  = lats.reduce((a, b) => a + b, 0) / lats.length;
  const lon  = lons.reduce((a, b) => a + b, 0) / lons.length;

  useEffect(() => {
    const icon = L.divIcon({
      className: 'survey-label',
      html: `<div>${feature.properties.survey_number}</div>`,
      iconSize: [40, 16],
      iconAnchor: [20, 8],
    });
    const marker = L.marker([lat, lon], { icon, interactive: false }).addTo(map);
    return () => marker.remove();
  }, [map, lat, lon, feature.properties.survey_number]);

  return null;
}

// ─── Fly-to controller ───────────────────────────────────────────────────────
function FlyToController({ onMapReady }) {
  const map = useMap();
  useEffect(() => {
    if (onMapReady) onMapReady(map);
    // Force Leaflet to recalculate container size after layout settles
    const t = setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(t);
  }, [map, onMapReady]);
  return null;
}

// ─── Popup Content ────────────────────────────────────────────────────────────
function ParcelPopup({ props }) {
  const cropColor  = CROP_COLORS[props.crop_type]     || '#94a3b8';
  const stageColor = STAGE_COLORS[props.growth_stage] || '#94a3b8';
  const conf       = Math.round((props.confidence || 0) * 100);
  const stageConf  = Math.round((props.stage_confidence || 0) * 100);

  return (
    <div style={{ minWidth: 220, fontFamily: 'inherit' }}>
      {/* Header */}
      <div
        className="flex items-center gap-2 mb-3 pb-2"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div
          className="w-3 h-3 rounded-sm flex-shrink-0"
          style={{ background: cropColor }}
        />
        <div>
          <div className="font-bold text-sm text-white">
            Survey No. {props.survey_number}
          </div>
          <div className="text-xs text-slate-400">
            {props.village} · {props.taluk}
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-1.5 text-xs">
        <PopRow label="Owner"       value={props.owner || '—'} />
        <PopRow label="Crop Type"   value={props.crop_type}
                valueColor={cropColor} bold />
        <PopRow label="Confidence"  value={`${conf}%`}
                valueColor={conf >= 80 ? '#4ade80' : '#fbbf24'} />
        <PopRow label="Growth Stage" value={props.growth_stage}
                valueColor={stageColor} />
        <PopRow label="Stage Conf." value={`${stageConf}%`} />
        <PopRow label="NDVI"        value={props.ndvi?.toFixed(3) ?? '—'}
                valueColor={props.ndvi > 0.5 ? '#4ade80' : props.ndvi > 0.2 ? '#fbbf24' : '#f87171'} />
        <PopRow label="Area"        value={`${props.area_hectares} ha`} />
        {props.season && <PopRow label="Season" value={props.season} />}
      </div>

      {/* NDVI bar */}
      <div className="mt-3">
        <div className="flex justify-between text-xs text-slate-400 mb-0.5">
          <span>NDVI</span>
          <span>{props.ndvi?.toFixed(3)}</span>
        </div>
        <div className="h-2 rounded-full bg-slate-700 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${Math.min((props.ndvi || 0) * 100, 100)}%`,
              background: `linear-gradient(90deg, #f87171, #fbbf24, #4ade80)`,
            }}
          />
        </div>
        <div className="flex justify-between text-xs text-slate-500 mt-0.5">
          <span>0</span><span>0.5</span><span>1</span>
        </div>
      </div>
    </div>
  );
}

function PopRow({ label, value, valueColor, bold }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-400">{label}</span>
      <span
        className={`${bold ? 'font-bold' : 'font-medium'}`}
        style={{ color: valueColor || '#e2e8f0' }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Drone marker icon (pulsing red circle via CSS DivIcon) ──────────────────
function createDroneIcon() {
  return L.divIcon({
    className: '',
    html: `
      <div style="position:relative;width:28px;height:28px">
        <!-- Outer pulse ring -->
        <div style="
          position:absolute;top:0;left:0;width:28px;height:28px;
          border-radius:50%;background:rgba(239,68,68,0.25);
          animation:pulse-ring 1.5s infinite;
        "></div>
        <!-- Inner solid dot -->
        <div style="
          position:absolute;top:6px;left:6px;width:16px;height:16px;
          border-radius:50%;background:#ef4444;
          border:2px solid #fff;box-shadow:0 0 6px rgba(239,68,68,0.8);
        "></div>
        <!-- Drone glyph -->
        <div style="
          position:absolute;top:4px;left:4px;width:20px;height:20px;
          display:flex;align-items:center;justify-content:center;
          font-size:10px;line-height:1;
        ">🚁</div>
      </div>
    `,
    iconSize:   [28, 28],
    iconAnchor: [14, 14],
    popupAnchor:[0, -16],
  });
}

// ─── Classification dot icon ─────────────────────────────────────────────────
function DroneMarker({ position, droneGps }) {
  const droneIcon = useMemo(() => createDroneIcon(), []);
  if (!position || !position.lat || !position.lon) return null;
  return (
    <Marker position={[position.lat, position.lon]} icon={droneIcon} zIndexOffset={1000}>
      <Popup>
        <div style={{ minWidth: 160, fontFamily: 'system-ui', fontSize: 12 }}>
          <div style={{ fontWeight: 700, color: '#ef4444', marginBottom: 6 }}>🚁 drone001</div>
          <div>Lat: {position.lat?.toFixed(6)}</div>
          <div>Lon: {position.lon?.toFixed(6)}</div>
          <div>Alt: {position.alt?.toFixed(1)} m</div>
          <div>Sats: {position.sats ?? '—'}</div>
          <div style={{ color: position.valid ? '#4ade80' : '#fbbf24', marginTop: 4 }}>
            GPS: {position.valid ? '✓ Fixed' : '⚠ No fix'}
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

// ─── Drone Socket.IO hook ─────────────────────────────────────────────────────
function useDroneLive() {
  const [droneGps,      setDroneGps]      = useState(null);
  const [flightPath,    setFlightPath]    = useState([]);
  const [classifications, setClassifs]    = useState([]);
  const socketRef = useRef(null);

  useEffect(() => {
    // Poll REST fallback first
    const pollGps = async () => {
      try {
        const [gpsR, resR] = await Promise.all([
          fetch(`${API_BASE}/api/drone/position`),
          fetch(`${API_BASE}/api/drone/results?limit=100`),
        ]);
        if (gpsR.ok) {
          const g = await gpsR.json();
          if (g.lat && g.lon) {
            setDroneGps(g);
            setFlightPath(prev => {
              const last = prev[prev.length - 1];
              if (!last || last[0] !== g.lat || last[1] !== g.lon) {
                return [...prev.slice(-999), [g.lat, g.lon]];
              }
              return prev;
            });
          }
        }
        if (resR.ok) {
          const d = await resR.json();
          setClassifs(d.features || []);
        }
      } catch { }
    };
    pollGps();
    const pollId = setInterval(pollGps, 5000);

    // Socket.IO real-time updates
    if (io) {
      try {
        const socket = io(API_BASE, { transports: ['websocket', 'polling'] });
        socketRef.current = socket;

        socket.on('drone_position', (data) => {
          if (!data.lat || !data.lon) return;
          setDroneGps(data);
          setFlightPath(prev => [...prev.slice(-999), [data.lat, data.lon]]);
        });

        socket.on('new_classification', (data) => {
          if (!data.lat || !data.lon) return;
          setClassifs(prev => {
            const feat = {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [data.lon, data.lat] },
              properties: data,
            };
            return [...prev.slice(-199), feat];
          });
        });
      } catch { }
    }

    return () => {
      clearInterval(pollId);
      socketRef.current?.disconnect();
    };
  }, []);

  return { droneGps, flightPath, classifications };
}

// ─── GPS location marker — parcel-style rectangle overlays ───────────────────
//   Each scan paints a coloured grid tile (≈100 m × 100 m) like a survey parcel.
//   As the user walks, tiles accumulate building a live crop-classification map.

/** Build a LatLngBounds rectangle for a tile centred at lat/lon */
function tileBounds(lat, lon, step = 0.0013) {
  const h = step / 2;
  return [
    [lat - h, lon - h],
    [lat + h, lon + h],
  ];
}

function zonePopupHtml(lat, lon, result, color, accuracy) {
  const crop  = result?.crop_type    || '…';
  const stage = result?.growth_stage || '…';
  const conf  = result ? Math.round(result.confidence * 100) : null;
  const ndvi  = result?.ndvi?.toFixed(3) ?? '—';
  return `
    <div style="min-width:210px;font-family:system-ui;font-size:12px">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;
                  padding-bottom:6px;border-bottom:1px solid rgba(255,255,255,0.12)">
        <div style="width:12px;height:12px;border-radius:3px;flex-shrink:0;
                    background:${color}"></div>
        <div>
          <div style="font-weight:700;font-size:13px;color:#fff">📍 GPS Classification</div>
          <div style="color:#94a3b8;font-size:10px">
            ${lat.toFixed(6)}°, ${lon.toFixed(6)}°${accuracy ? `  ±${Math.round(accuracy)} m` : ''}
          </div>
        </div>
      </div>
      ${result ? `
      <table style="width:100%;font-size:11px;border-collapse:collapse">
        <tr><td style="color:#94a3b8;padding:2px 0">Crop Type</td>
            <td style="text-align:right;color:${color};font-weight:700;padding:2px 0">${crop}</td></tr>
        <tr><td style="color:#94a3b8;padding:2px 0">Growth Stage</td>
            <td style="text-align:right;color:#e2e8f0;padding:2px 0">${stage}</td></tr>
        <tr><td style="color:#94a3b8;padding:2px 0">Confidence</td>
            <td style="text-align:right;padding:2px 0;color:${conf >= 80 ? '#4ade80' : '#fbbf24'}">${conf}%</td></tr>
        <tr><td style="color:#94a3b8;padding:2px 0">NDVI</td>
            <td style="text-align:right;color:#e2e8f0;padding:2px 0">${ndvi}</td></tr>
        <tr><td style="color:#94a3b8;padding:2px 0">Source</td>
            <td style="text-align:right;color:#64748b;padding:2px 0">Sentinel-2</td></tr>
      </table>
      <div style="margin-top:8px">
        <div style="height:5px;border-radius:3px;background:#1e293b;overflow:hidden">
          <div style="width:${Math.min((result.ndvi||0)*100,100)}%;height:100%;border-radius:3px;
                      background:linear-gradient(90deg,#ef4444,#fbbf24,#22c55e)"></div>
        </div>
      </div>
      ` : '<div style="color:#fbbf24;font-size:11px;padding:4px 0">🔍 Classifying…</div>'}
    </div>`;
}

function GpsLocationMarker({ gpsMarker, gpsTrail, gpsHistory }) {
  const map = useMap();

  // ── Current position: rectangle tile + pulsing pin ──────────────────────────
  useEffect(() => {
    if (!gpsMarker?.lat || !gpsMarker?.lon) return;
    const { lat, lon, result, accuracy } = gpsMarker;
    const cropColor = (result && CROP_COLORS[result.crop_type]) || '#3b82f6';

    // Gently pan to keep GPS pin visible — don't force zoom change
    if (!map.getBounds().contains([lat, lon])) {
      map.panTo([lat, lon], { animate: true, duration: 0.5 });
    }

    const layers = [];

    // Accuracy ring (thin blue dashed)
    if (accuracy > 0) {
      layers.push(L.circle([lat, lon], {
        radius: accuracy, color: '#60a5fa', fillColor: '#60a5fa',
        fillOpacity: 0.06, weight: 1, dashArray: '5 4', interactive: false,
      }).addTo(map));
    }

    // ── Main classification rectangle — fill only, no border ──
    const rect = L.rectangle(tileBounds(lat, lon), {
      stroke:      false,
      fillColor:   cropColor,
      fillOpacity: 0.55,
    }).addTo(map);
    rect.bindPopup(zonePopupHtml(lat, lon, result, cropColor, accuracy),
      { maxWidth: 260, className: 'crop-popup' });
    layers.push(rect);

    // ── Pulsing 📍 pin at exact GPS point ──
    const icon = L.divIcon({
      className: '',
      html: `<div style="position:relative;width:34px;height:34px">
        <div style="position:absolute;inset:0;border-radius:50%;
          background:${cropColor}44;animation:pulse-ring 1.4s infinite"></div>
        <div style="position:absolute;top:5px;left:5px;width:24px;height:24px;
          border-radius:50%;background:${cropColor};border:3px solid #fff;
          box-shadow:0 0 10px ${cropColor}cc,0 2px 8px rgba(0,0,0,0.7);
          display:flex;align-items:center;justify-content:center;font-size:12px">📍</div>
      </div>`,
      iconSize: [34, 34], iconAnchor: [17, 17],
    });
    layers.push(
      L.marker([lat, lon], { icon, zIndexOffset: 3000, interactive: false }).addTo(map)
    );

    return () => layers.forEach(l => { try { l.remove(); } catch {} });
  }, [map, gpsMarker]);

  // ── Walking trail polyline ───────────────────────────────────────────────────
  useEffect(() => {
    if (!gpsTrail || gpsTrail.length < 2) return;
    const line = L.polyline(
      gpsTrail.map(p => [p.lat, p.lon]),
      { color: '#22d3ee', weight: 2.5, opacity: 0.9, dashArray: '8 5' }
    ).addTo(map);
    return () => { try { line.remove(); } catch {} };
  }, [map, gpsTrail]);

  // ── Historical classification rectangles — full grid overlay ─────────────────
  useEffect(() => {
    if (!gpsHistory || gpsHistory.length === 0) return;
    const layers = gpsHistory.map(h => {
      const c = CROP_COLORS[h.result?.crop_type] || '#3b82f6';

      const rect = L.rectangle(tileBounds(h.lat, h.lon, h.step || 0.0013), {
        stroke:      false,
        fillColor:   c,
        fillOpacity: 0.50,
      }).addTo(map);
      rect.bindPopup(
        zonePopupHtml(h.lat, h.lon, h.result, c, null),
        { maxWidth: 260, className: 'crop-popup' }
      );
      return rect;
    });

    return () => layers.forEach(l => { try { l.remove(); } catch {} });
  }, [map, gpsHistory]);

  return null;
}

export default function MapDashboard({ filters, viewMode, onParcelClick, onMapReady, gpsMarker, gpsTrail, gpsHistory, simPos, simPath }) {
  const geoJsonRef = useRef(null);
  const popupRef   = useRef(null);

  // Drone live state (real MQTT/Socket.IO)
  const { droneGps, flightPath, classifications } = useDroneLive();
  const [selectedClassif, setSelectedClassif] = useState(null);

  // Simulation props take priority over real backend data
  const activeDronePos   = simPos  || droneGps;
  const activeFlightPath = (simPath && simPath.length > 0) ? simPath : flightPath;

  // Filter parcels based on sidebar selection
  const filteredFeatures = useMemo(() => {
    let features = parcelsData.features;
    if (filters.village) {
      features = features.filter(f => f.properties.village === filters.village);
    }
    if (filters.surveyNumber) {
      features = features.filter(f =>
        f.properties.survey_number.includes(filters.surveyNumber)
      );
    }
    return features;
  }, [filters.village, filters.surveyNumber]);

  const filteredGeoJson = useMemo(() => ({
    ...parcelsData,
    features: filteredFeatures,
  }), [filteredFeatures]);

  // Restyle when viewMode or selection changes
  const onEachFeature = useCallback((feature, layer) => {
    const props = feature.properties;

    layer.on({
      click: (e) => {
        L.DomEvent.stopPropagation(e);
        onParcelClick && onParcelClick(props);
        layer.setStyle({
          weight: 3,
          color: '#ffffff',
          fillOpacity: 0.75,
        });
        layer.openPopup();
      },
      mouseover: (e) => {
        layer.setStyle({ weight: 2.5, fillOpacity: 0.6 });
        layer.bringToFront();
      },
      mouseout: (e) => {
        if (geoJsonRef.current) {
          geoJsonRef.current.resetStyle(layer);
        }
      },
    });

    // Bind popup
    layer.bindPopup(
      () => {
        const div = document.createElement('div');
        // We'll mount a simple HTML popup since we can't use ReactDOM in callback
        const p = feature.properties;
        const cropColor  = CROP_COLORS[p.crop_type]     || '#94a3b8';
        const stageColor = STAGE_COLORS[p.growth_stage] || '#94a3b8';
        const ndviPct    = Math.min((p.ndvi || 0) * 100, 100).toFixed(1);
        const ndviColor  = p.ndvi > 0.5 ? '#4ade80' : p.ndvi > 0.2 ? '#fbbf24' : '#f87171';

        div.innerHTML = `
          <div style="min-width:220px;font-family:system-ui,sans-serif;font-size:12px">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;
                        padding-bottom:8px;border-bottom:1px solid rgba(255,255,255,0.1)">
              <div style="width:12px;height:12px;border-radius:3px;flex-shrink:0;
                          background:${cropColor}"></div>
              <div>
                <div style="font-weight:700;font-size:13px;color:#fff">
                  Survey No. ${p.survey_number}</div>
                <div style="color:#94a3b8;font-size:11px">${p.village} · ${p.taluk}</div>
              </div>
            </div>
            <table style="width:100%;border-collapse:collapse">
              ${[
                ['Owner', p.owner || '—', '#e2e8f0', false],
                ['Crop Type', p.crop_type, cropColor, true],
                ['Confidence', `${Math.round((p.confidence||0)*100)}%`,
                  p.confidence>=0.8?'#4ade80':'#fbbf24', false],
                ['Growth Stage', p.growth_stage, stageColor, false],
                ['NDVI', p.ndvi?.toFixed(3)??'—', ndviColor, false],
                ['Area', `${p.area_hectares} ha`, '#e2e8f0', false],
                ['Season', p.season||'—', '#e2e8f0', false],
              ].map(([label, val, color, bold]) => `
                <tr>
                  <td style="color:#94a3b8;padding:2px 0">${label}</td>
                  <td style="text-align:right;color:${color};
                             font-weight:${bold?'700':'500'};padding:2px 0">
                    ${val}
                  </td>
                </tr>
              `).join('')}
            </table>
            <div style="margin-top:10px">
              <div style="display:flex;justify-content:space-between;
                          color:#64748b;margin-bottom:2px">
                <span>NDVI</span><span>${p.ndvi?.toFixed(3)}</span>
              </div>
              <div style="height:6px;border-radius:3px;background:#334155;overflow:hidden">
                <div style="width:${ndviPct}%;height:100%;border-radius:3px;
                            background:linear-gradient(90deg,#f87171,#fbbf24,#4ade80)"></div>
              </div>
            </div>
          </div>
        `;
        return div;
      },
      { maxWidth: 280, className: 'crop-popup' }
    );
  }, [onParcelClick]);

  const styleFunction = useCallback(
    (feature) => getParcelStyle(feature, viewMode, filters.surveyNumber),
    [viewMode, filters.surveyNumber]
  );

  return (
    <MapContainer
      center={MAP_CENTER}
      zoom={DEFAULT_ZOOM}
      style={{ height: '100%', width: '100%' }}
      zoomControl={false}
      attributionControl={true}
    >
      {/* ── Fly-to hook & map ref ───────────────────────────────────────── */}
      <FlyToController onMapReady={onMapReady} />

      {/* ── Zoom & Scale controls ───────────────────────────────────────── */}
      <ZoomControl position="bottomright" />
      <ScaleControl position="bottomleft" imperial={false} />

      {/* ── Base layer: Esri World Imagery (satellite) ──────────────────── */}
      <TileLayer
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
        maxZoom={19}
      />

      {/* ── Hybrid label overlay ────────────────────────────────────────── */}
      <TileLayer
        url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
        opacity={0.6}
        maxZoom={19}
      />

      {/* ── GeoJSON parcels ─────────────────────────────────────────────── */}
      <GeoJSON
        key={`${viewMode}-${filters.village}-${filters.surveyNumber}`}
        data={filteredGeoJson}
        style={styleFunction}
        onEachFeature={onEachFeature}
        ref={geoJsonRef}
      />

      {/* ── Drone flight path polyline ───────────────────────────────────── */}
      {activeFlightPath.length > 1 && (
        <Polyline
          positions={activeFlightPath}
          pathOptions={{
            color:     '#ef4444',
            weight:    2,
            opacity:   0.7,
            dashArray: '6 4',
          }}
        />
      )}

      {/* ── Classification dots (one per drone image) ───────────────────── */}
      {classifications.map((feat, i) => {
        const p    = feat.properties || {};
        const color= CROP_COLORS[p.crop_type] || '#94a3b8';
        const pos  = [feat.geometry.coordinates[1], feat.geometry.coordinates[0]];
        return (
          <CircleMarker
            key={`classif-${i}-${p.seq}`}
            center={pos}
            radius={6}
            pathOptions={{
              fillColor: color,
              fillOpacity: 0.9,
              color: '#fff',
              weight: 1.5,
            }}
            eventHandlers={{
              click: () => setSelectedClassif(feat),
            }}
          >
            <Popup>
              <DroneClassifPopup feat={feat} />
            </Popup>
          </CircleMarker>
        );
      })}

      {/* ── GPS location marker (browser / phone geolocation) ───────────── */}
      <GpsLocationMarker gpsMarker={gpsMarker} gpsTrail={gpsTrail} gpsHistory={gpsHistory} />

      {/* ── Live drone marker ────────────────────────────────────────────── */}
      <DroneMarker position={activeDronePos} />

      {/* ── Map overlay info ─────────────────────────────────────────────── */}
      <MapOverlay
        count={filteredFeatures.length}
        viewMode={viewMode}
        droneActive={!!activeDronePos?.valid}
        classifCount={classifications.length}
      />
    </MapContainer>
  );
}

// ─── Corner overlay showing parcel + drone count ─────────────────────────────
function MapOverlay({ count, viewMode, droneActive, classifCount }) {
  return (
    <div
      className="absolute top-3 left-3 z-[1000] glass rounded-lg px-3 py-2 text-xs space-y-1"
      style={{ pointerEvents: 'none' }}
    >
      <div className="text-green-400 font-bold">{count} Parcels</div>
      <div className="text-slate-400">
        View: {viewMode === 'crop' ? 'Crop Type' : 'Growth Stage'}
      </div>
      {/* Drone status badge */}
      <div className="flex items-center gap-1.5 pt-0.5 border-t border-slate-700/50">
        <div
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: droneActive ? '#ef4444' : '#475569' }}
        />
        <span style={{ color: droneActive ? '#fca5a5' : '#64748b' }}>
          {droneActive ? `🚁 Live` : '🚁 Offline'}
        </span>
        {classifCount > 0 && (
          <span className="text-slate-500">· {classifCount} pts</span>
        )}
      </div>
    </div>
  );
}

// ─── Drone classification popup content ──────────────────────────────────────
function DroneClassifPopup({ feat }) {
  const p = feat?.properties || {};
  const cropColor  = CROP_COLORS[p.crop_type]     || '#94a3b8';
  const stageColor = STAGE_COLORS[p.growth_stage] || '#94a3b8';
  const conf       = Math.round((p.confidence || 0) * 100);

  return (
    <div style={{ minWidth: 200, fontFamily: 'system-ui', fontSize: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        marginBottom: 8, paddingBottom: 6,
        borderBottom: '1px solid rgba(255,255,255,0.1)',
      }}>
        <div style={{
          width: 12, height: 12, borderRadius: 3,
          background: cropColor, flexShrink: 0,
        }} />
        <div>
          <div style={{ fontWeight: 700, color: '#fff', fontSize: 13 }}>
            🚁 Drone Capture #{p.seq}
          </div>
          <div style={{ color: '#94a3b8', fontSize: 11 }}>
            {p.timestamp || ''}
          </div>
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        {[
          ['Crop',       p.crop_type,    cropColor,  true],
          ['Stage',      p.growth_stage, stageColor, false],
          ['Confidence', `${conf}%`,     conf >= 80 ? '#4ade80' : '#fbbf24', false],
          ['NDVI',       p.ndvi_value?.toFixed(3) ?? '—',
            p.ndvi_value > 0.5 ? '#4ade80' : '#fbbf24', false],
          ['Lat',        p.lat?.toFixed(6), '#e2e8f0', false],
          ['Lon',        p.lon?.toFixed(6), '#e2e8f0', false],
          ['Alt',        p.alt ? `${p.alt?.toFixed(1)} m` : '—', '#e2e8f0', false],
        ].map(([lbl, val, color, bold]) => (
          <tr key={lbl}>
            <td style={{ color: '#94a3b8', padding: '2px 0' }}>{lbl}</td>
            <td style={{
              textAlign: 'right', color, padding: '2px 0',
              fontWeight: bold ? 700 : 500,
            }}>{val}</td>
          </tr>
        ))}
      </table>
      {/* Image thumbnail link */}
      {p.filename && (
        <div style={{ marginTop: 8 }}>
          <a
            href={`${API_BASE}/api/drone/images/${p.filename}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#60a5fa', fontSize: 11, textDecoration: 'underline' }}
          >
            📷 View captured image
          </a>
        </div>
      )}
    </div>
  );
}
